import { faCircleCheck, faListCheck } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV4,
  ButtonV2,
  RadioButtonsV2,
  RadioButtonType,
  TextInputV4,
} from '@labrav/react-components'
import { useStyles } from './PricingTool.styles'
import { FormikHandlers, FormikProps, useFormikContext } from 'formik'
import {
  PriceTypes,
  PrivateOffer,
} from '../../../../modules/privateOffer/types'
import { useState, useMemo } from 'react'
import { getDiscountedPriceValue, isNonEmptyNumber } from '@labrav/common'
import { Product } from '../../../../modules/productsListing/types'
import { isNull } from 'lodash'
import { ThreeDecimalPointRegex } from '../../../../common/utils/constants'
import {
  threeDecimalMsg,
  valueGreaterThanZeroMsg,
} from '../../../../common/utils/messagesContants'
import { Typography } from '@mui/material'

export const PricingTool = ({ product }: { product: Product }) => {
  const { values, handleChange, setFieldValue } =
    useFormikContext() as FormikProps<PrivateOffer>

  const { classes } = useStyles()

  const [discountUpfrontPrice, setDiscountUpfrontPrice] = useState<
    number | null
  >(null)
  const [discountUsagePrice, setDiscountUsagePrice] = useState<number | null>(
    null
  )
  const [upfrontPrice, setUpfrontPrice] = useState<number | null>(null)
  const [usagePrice, setUsagePrice] = useState<number | null>(null)
  const [quantity, setQuantity] = useState<number | null>(null)

  const [discountUpfrontPriceError, setDiscountUpfrontPriceError] =
    useState<string>('')
  const [discountUsagePriceError, setDiscountUsagePriceError] =
    useState<string>('')
  const [upfrontPriceError, setUpfrontPriceError] = useState<string>('')
  const [usagePriceError, setUsagePriceError] = useState<string>('')
  const [quantityError, setQuantityError] = useState<string>('')
  const [isApplied, setIsApplied] = useState<boolean>(false)

  const pricingModel = values?.pricingModel

  const productDimensions = product?.productDimensions

  const resetState = () => {
    setDiscountUpfrontPrice(null)
    setDiscountUsagePrice(null)
    setUpfrontPrice(null)
    setUsagePrice(null)
    setQuantity(null)
    setDiscountUpfrontPriceError('')
    setDiscountUsagePriceError('')
    setUpfrontPriceError('')
    setUsagePriceError('')
    setQuantityError('')
    setIsApplied(false)
  }

  const validateQuantityValue = (value: number | null): string => {
    if (value !== null && value < 0) {
      return 'value must be greater than or equal to 0'
    }
    return ''
  }

  const validateDiscountValue = (value: number | null): string => {
    if (value !== null && (value < 0 || value > 100)) {
      return 'value must be valid discount percentage.'
    }
    return ''
  }

  const validatePriceValue = (
    inputString: string,
    numericValue: number | null
  ): string => {
    if (inputString === '') {
      return ''
    }
    // Allow inputs ending with decimal point (intermediate typing state)
    const normalizedInput = inputString.endsWith('.')
      ? inputString.slice(0, -1)
      : inputString
    if (
      normalizedInput !== '' &&
      !ThreeDecimalPointRegex.test(normalizedInput)
    ) {
      return threeDecimalMsg
    }
    if (numericValue !== null && numericValue < 0) {
      return valueGreaterThanZeroMsg
    }
    return ''
  }
  const handleApply = async () => {
    if (
      discountUpfrontPriceError ||
      discountUsagePriceError ||
      quantityError ||
      upfrontPriceError ||
      usagePriceError
    ) {
      return
    }
    const dimensions = values?.dimensions
    if (dimensions?.length) {
      const updatedDimensions = dimensions.map(dimension => {
        const productDimension = productDimensions?.find(
          d => d.id === dimension.dimensionId
        )
        const isDiscount = values?.pricingToolType === 'discount'
        const pricingModel = values?.pricingModel

        const updatedPrice = getDiscountedPriceValue({
          price: (productDimension?.price as number) || 0,
          discount: discountUpfrontPrice || 0,
        })
        const updatedUsagePrice = getDiscountedPriceValue({
          price:
            (productDimension?.prices as PriceTypes)?.additionalUsagePrice || 0,
          discount: discountUsagePrice || 0,
        })

        return {
          ...dimension,
          ...(pricingModel === 'upfront-payment'
            ? {
                price: isDiscount
                  ? updatedPrice
                  : !isNull(upfrontPrice) && isNonEmptyNumber(upfrontPrice)
                    ? upfrontPrice
                    : productDimension?.price,
              }
            : {}),
          ...(pricingModel === 'installment-plan' && !isDiscount
            ? {
                quantity: quantity,
              }
            : {}),
          additionalUsagePrice: isDiscount
            ? updatedUsagePrice
            : !isNull(usagePrice) && isNonEmptyNumber(usagePrice)
              ? usagePrice
              : (productDimension?.prices as PriceTypes)?.additionalUsagePrice,
        }
      })
      await setFieldValue('dimensions', updatedDimensions)
      setIsApplied(true)
    }
  }

  const getCheckedKey = () => {
    if (values?.pricingToolType === 'value') {
      return 'apply-value-to-all-inputs'
    }
    if (values?.pricingToolType === 'discount') {
      return 'apply-discount-to-public-pricing'
    }
    return ''
  }

  const getPricingToolTypeFromKey = (key: string): string => {
    if (key === 'apply-value-to-all-inputs') {
      return 'value'
    }
    if (key === 'apply-discount-to-public-pricing') {
      return 'discount'
    }
    return ''
  }

  const isInputEmpty = useMemo(() => {
    if (values?.pricingToolType === 'discount') {
      if (pricingModel === 'upfront-payment') {
        return discountUpfrontPrice === null && discountUsagePrice === null
      }
      return discountUsagePrice === null
    }
    if (values?.pricingToolType === 'value') {
      if (pricingModel === 'installment-plan') {
        return quantity === null && usagePrice === null
      }
      if (pricingModel === 'upfront-payment') {
        return upfrontPrice === null && usagePrice === null
      }
      return usagePrice === null
    }
    return true
  }, [
    values?.pricingToolType,
    pricingModel,
    discountUpfrontPrice,
    discountUsagePrice,
    quantity,
    upfrontPrice,
    usagePrice,
  ])

  const handleCheckboxChange =
    (handleChange: FormikHandlers['handleChange']) =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const pricingToolType = getPricingToolTypeFromKey(e.target.value)
      await handleChange({
        target: {
          name: 'pricingToolType',
          value: pricingToolType,
        },
      })
      await resetState()
    }
  return (
    <div>
      <BoxV4
        heading={'Pricing tool'}
        headingIcon={
          <FontAwesomeIcon
            icon={faListCheck}
            style={{ width: '18px', height: '14px' }}
          />
        }
        isOptional={true}
      >
        <div className={classes.pricingToolContainer}>
          <div className={classes.sectionContainer}>
            <RadioButtonsV2
              radioButtonMappings={[
                {
                  key: 'apply-discount-to-public-pricing',
                  label: 'Apply a discount to public pricing',
                  type: RadioButtonType.STANDARD,
                  disabled: !!values?.currency && values?.currency !== 'USD',
                  disabledTooltip:
                    !!values?.currency && values?.currency !== 'USD'
                      ? 'This option is only available for the USD currency.'
                      : '',
                },
                {
                  key: 'apply-value-to-all-inputs',
                  label: 'Apply a value to all inputs',
                  type: RadioButtonType.STANDARD,
                },
              ]}
              checkedValue={{
                key: getCheckedKey(),
                value: getCheckedKey(),
              }}
              handleChange={handleCheckboxChange(handleChange)}
            />
            <div className={classes.fieldRow}>
              {values?.pricingToolType === 'discount' && (
                <>
                  {pricingModel === 'upfront-payment' && (
                    <div className={classes.fieldWidth}>
                      <TextInputV4
                        keyName="upfront-discount"
                        title="Upfront discount(%)"
                        type="number"
                        placeHolder=""
                        value={discountUpfrontPrice || ''}
                        onChange={e => {
                          const numValue =
                            e.target.value === ''
                              ? null
                              : Number(e.target.value)
                          setDiscountUpfrontPrice(numValue)
                          setDiscountUpfrontPriceError(
                            validateDiscountValue(numValue)
                          )
                          setIsApplied(false)
                        }}
                        isError={!!discountUpfrontPriceError}
                        errorText={discountUpfrontPriceError}
                        isReadOnly={false}
                        alignRight
                        isRequired={false}
                        showSpinner={false}
                        keyToPrevent={['+', '-', 'e']}
                      />
                    </div>
                  )}
                  <div className={classes.fieldWidth}>
                    <TextInputV4
                      keyName="usage-discount"
                      title="Usage discount(%)"
                      type="number"
                      placeHolder=""
                      value={discountUsagePrice || ''}
                      onChange={e => {
                        const numValue =
                          e.target.value === '' ? null : Number(e.target.value)
                        setDiscountUsagePrice(numValue)
                        setDiscountUsagePriceError(
                          validateDiscountValue(numValue)
                        )
                        setIsApplied(false)
                      }}
                      isError={!!discountUsagePriceError}
                      errorText={discountUsagePriceError}
                      isReadOnly={false}
                      alignRight
                      isRequired={false}
                      showSpinner={false}
                      keyToPrevent={['+', '-', 'e']}
                    />
                  </div>
                </>
              )}
              {values?.pricingToolType === 'value' && (
                <>
                  {pricingModel === 'installment-plan' && (
                    <div className={classes.fieldWidth}>
                      <TextInputV4
                        keyName="upfront-price"
                        title="Quantity "
                        type="number"
                        placeHolder=""
                        value={quantity || ''}
                        onChange={e => {
                          const numValue =
                            e.target.value === ''
                              ? null
                              : Number(e.target.value)
                          setQuantity(numValue)
                          setQuantityError(validateQuantityValue(numValue))
                          setIsApplied(false)
                        }}
                        isError={!!quantityError}
                        errorText={quantityError}
                        isReadOnly={false}
                        isRequired={false}
                        alignRight
                        showSpinner={false}
                        keyToPrevent={['+', '-', 'e', '.']}
                      />
                    </div>
                  )}
                  {pricingModel === 'upfront-payment' && (
                    <div className={classes.fieldWidth}>
                      <TextInputV4
                        keyName="upfront-price"
                        title="Upfront price"
                        type="number"
                        placeHolder=""
                        value={upfrontPrice || ''}
                        onChange={e => {
                          const inputValue = e.target.value
                          const numValue =
                            inputValue === '' ? null : Number(inputValue)
                          setUpfrontPrice(numValue)
                          setUpfrontPriceError(
                            validatePriceValue(inputValue, numValue)
                          )
                          setIsApplied(false)
                        }}
                        isError={!!upfrontPriceError}
                        errorText={upfrontPriceError}
                        isReadOnly={false}
                        isRequired={false}
                        alignRight
                        showSpinner={false}
                        keyToPrevent={['+', '-', 'e']}
                      />
                    </div>
                  )}
                  <div className={classes.fieldWidth}>
                    <TextInputV4
                      keyName="usage-price"
                      title="Usage price"
                      type="number"
                      placeHolder=""
                      value={usagePrice || ''}
                      onChange={e => {
                        const inputValue = e.target.value
                        const numValue =
                          inputValue === '' ? null : Number(inputValue)
                        setUsagePrice(numValue)
                        setUsagePriceError(
                          validatePriceValue(inputValue, numValue)
                        )
                        setIsApplied(false)
                      }}
                      isError={!!usagePriceError}
                      errorText={usagePriceError}
                      isReadOnly={false}
                      isRequired={false}
                      alignRight
                      showSpinner={false}
                      keyToPrevent={['+', '-', 'e']}
                    />
                  </div>
                </>
              )}
              {(values?.pricingToolType === 'value' ||
                values?.pricingToolType === 'discount') && (
                <div className={classes.applyButton}>
                  <ButtonV2
                    smallVariant
                    styleType="outlined"
                    onClick={handleApply}
                    size="medium"
                  >
                    Apply
                  </ButtonV2>
                </div>
              )}
            </div>
          </div>
          {isApplied && !isInputEmpty && (
            <div
              className={classes.successTextContainer}
              data-testid="success-pricing-applied"
            >
              <FontAwesomeIcon icon={faCircleCheck} className={classes.check} />
              <Typography className={classes.successText}>
                Prices for all instance types below have been updated
                successfully.
              </Typography>
            </div>
          )}
        </div>
      </BoxV4>
    </div>
  )
}
